import React, {useRef} from "react";
import Header from "../../../sections/shared/header/Header";
import Layout from "../../ui/layout/Layout";
import ContactBar from "../../../sections/shared/contactBar/ContactBar";
import Footer from "../../../sections/shared/footer/Footer";
import {graphql, HeadProps, PageProps} from "gatsby";
import {AllStrapiArticles, MarkdownRemark, StrapiArticle} from "../../../types/BlogTypes";
import './blogArticle.scss';
import {getLighterImageCloudinary} from "../../../services/utils/cloudinary";
import Card from "../../ui/card/Card";
import ArticleCard from "../../../sections/blog/articleCard/ArticleCard";
import Seo from "../../ui/seo/Seo";
import BlogNewsletterModal from "../../../sections/blog/blogNewsletterModal/BlogNewsletterModal";

interface BlogArticleDataProps {
    article: StrapiArticle,
    markdownRemark: MarkdownRemark;
    relatedArticles: AllStrapiArticles,
}

const BlogArticle = ({data}: PageProps<BlogArticleDataProps>) => {

    const {article, markdownRemark, relatedArticles} = data;
    const {html} = markdownRemark;
    const bodyRef = useRef(null);

    const {
        author,
        titleImage: image,
        readtime,
        categories,
        title,
        publicationDate,
    } = article;
    const { function: position, email, fullname, picture } = author;
    const dateString = new Date(publicationDate).toLocaleDateString('fr-FR', {year: 'numeric', month: 'long', day: 'numeric'});

    return (
        <>
            <Header />
            <main>
                <Layout bgType='blog'>
                    <section className='article'>
                        <div className='articleHeader'>
                            <img
                                src={getLighterImageCloudinary(image, 530, 530)}
                                alt={title}
                                title={`Illustration : ${title}`}
                            />
                            <div className='articleInfos'>
                                <p>
                                    {`${categories?.[0]?.name} · ${readtime} min`}
                                </p>
                                <h1>{title}</h1>
                                <p>
                                    Publié le {dateString}
                                </p>
                                <Card className='authorBlock'>
                                    <img src={getLighterImageCloudinary(picture, 200, 200)} alt={fullname} title={fullname} />
                                    <div>
                                        <p>{fullname}</p>
                                        <p>{position}</p>
                                        {
                                            email ? (
                                                <a
                                                    title={`Envoyer un email à ${fullname}`}
                                                    aria-label={`Envoyer un email à ${fullname}`}
                                                    href={`mailto:${email}`}
                                                    target='_top'
                                                >
                                                    {`> Entrer en contact`}
                                                </a>
                                            ) : null
                                        }
                                    </div>
                                </Card>
                            </div>
                        </div>
                        <div
                            ref={bodyRef}
                            dangerouslySetInnerHTML={{ __html: html }}
                            className='markdown'
                        />
                    </section>
                    <div className='relatedArticles'>
                        {
                            relatedArticles?.edges?.map(article => <ArticleCard article={article.node} />)
                        }
                    </div>
                </Layout>
                <BlogNewsletterModal />
            </main>
            <ContactBar />
            <Footer />
        </>
    );
};

export default BlogArticle;

export const Head = ({data: {article}, location}: HeadProps<BlogArticleDataProps>) => (
    <Seo
        title={`${article?.title}`}
        description={article?.description}
        image={article?.titleImage}
        pathname={location.pathname}
    />
);

export const query = graphql`
    query ($language: String!, $id: String!, $remarkId: String!, $category: Int) {
        locales: allLocale(filter: {ns: { in: ["common", "blog"] }, language: {eq: $language}}) {
          edges {
            node {
              ns
              data
              language
            }
          }
        }
        article: strapiArticle(id: {eq: $id}) {
            id
			publicationDate: publication_date
			path
			readtime
			title
			titleImage: title_image
			author {
				id
				email
				fullname
				function
				picture
				email
			}
			description
			categories {
				name
			}
			tags {
				name
				id
			}
        }
        markdownRemark(parent: {id: { eq: $remarkId }}) {
			html
			id
		}
		relatedArticles: allStrapiArticle(
			limit: 3
			sort: { publication_date: DESC }
			filter: {
				categories: { elemMatch: { id: { eq: $category } } }
				id: { ne: $id }
			}
		) {
			edges {
				node {
					id
					readtime
					title
					titleImage: title_image
					article_path
					categories {
						name
					}
				}
			}
		}
  }
`;